import { Component } from '@theme/component';
import { isTouchDevice } from '@theme/utilities';

/**
 * How long the panel stays open after the pointer leaves the item, in hover mode.
 * Matches HOVER_COMMIT_DELAY_MS in header-menu.js so both menus feel the same.
 */
const HOVER_CLOSE_DELAY_MS = 150;

/** Sub-pixel slack, so a rounding difference doesn't read as an overflow. */
const OVERFLOW_TOLERANCE = 1;

const COLUMNS_SELECTOR = '.header__columns';
const ITEM_SELECTOR = '.wce-dropdown-menu__item';
const TRIGGER_SELECTOR = '.wce-dropdown-menu__trigger';
const PANEL_SELECTOR = '.wce-dropdown-menu__panel';

/**
 * A simple, traditional dropdown navigation for the desktop header.
 *
 * Deliberately does NOT implement a roving tabindex or arrow-key menu navigation:
 * `.cursor/rules/dropdown-navigation-accessibility.mdc` requires navigation to stay in
 * the natural tab order, with Enter/Space on the launcher and Escape to close. Enter and
 * Space are handled natively by the `<button>` launcher (they fire `click`), so this
 * component only adds Escape, hover, focus-out and outside-press handling on top.
 *
 * @typedef {object} WceDropdownMenuRefs
 * @property {HTMLUListElement} list - The top-level list. Measured for row promotion.
 * @property {HTMLElement[]} [panels] - The dropdown panels, one per launcher.
 *
 * @extends {Component<WceDropdownMenuRefs>}
 */
class WceDropdownMenuComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {HTMLElement | null} */
  #columns = null;

  /** @type {ResizeObserver | null} */
  #layoutObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('pointerdown', this.#handleOutsidePress, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    this.#applyBaseMenuStyle();

    this.#columns = this.closest(COLUMNS_SELECTOR);

    if (this.#columns) {
      this.#layoutObserver = new ResizeObserver(this.#syncRowLayout);
      this.#layoutObserver.observe(this.#columns);
    }

    // A webfont swapping in changes the menu's width, so re-measure once it lands.
    document.fonts?.ready.then(this.#syncRowLayout);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;
    clearTimeout(this.#closeTimer);

    super.disconnectedCallback();
  }

  /**
   * Whether hovering a parent item opens its panel. Set by the `open_on` block setting.
   *
   * @returns {boolean}
   */
  get opensOnHover() {
    return this.dataset.openOn !== 'click';
  }

  /**
   * Toggles the panel belonging to the launcher that was clicked. Bound with
   * `on:click` on the launcher, so it also covers Enter and Space, which a native
   * `<button>` reports as a click.
   *
   * @param {PointerEvent} event - The click event.
   */
  toggle(event) {
    const pair = this.#pairFrom(event.target);
    if (!pair) return;

    if (pair.panel.hidden) {
      this.#open(pair);
    } else {
      this.#close(pair);
    }
  }

  /**
   * Handles keys pressed while a launcher has focus.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  triggerKeydown(event) {
    if (event.key !== 'Escape') return;

    const pair = this.#pairFrom(event.target);
    if (!pair || pair.panel.hidden) return;

    this.#close(pair);
    pair.trigger.focus();
  }

  /**
   * Handles keys pressed inside an open panel. The rule requires the content area to
   * close on Escape and return focus to its launcher, not just the launcher itself.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  panelKeydown(event) {
    if (event.key !== 'Escape') return;

    const pair = this.#pairFrom(event.target);
    if (!pair) return;

    this.#close(pair);
    pair.trigger.focus();
  }

  /**
   * Opens a panel when the pointer enters its item, in hover mode. Bound on the item,
   * so the panel — a DOM descendant — counts as part of the same hover region.
   *
   * Never bound to `focus`: the rule states "Do not open dropdown on keyboard focus."
   *
   * @param {PointerEvent} event - The pointerenter event.
   */
  pointerEnter(event) {
    clearTimeout(this.#closeTimer);
    if (!this.opensOnHover) return;

    const pair = this.#pairFrom(event.target);
    if (pair) this.#open(pair);
  }

  /**
   * Closes the panel a short delay after the pointer leaves its item, so a pointer
   * travelling diagonally from the launcher into the panel doesn't lose it.
   *
   * @param {PointerEvent} event - The pointerleave event.
   */
  pointerLeave(event) {
    if (!this.opensOnHover) return;

    const pair = this.#pairFrom(event.target);
    if (!pair) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#close(pair), HOVER_CLOSE_DELAY_MS);
  }

  /**
   * Resolves the launcher and panel belonging to whichever item contains `target`.
   *
   * @param {EventTarget | null} target - Any element inside a dropdown item.
   * @returns {{ trigger: HTMLButtonElement, panel: HTMLElement } | null}
   */
  #pairFrom(target) {
    if (!(target instanceof Element)) return null;

    const item = target.closest(ITEM_SELECTOR);
    if (!item) return null;

    const trigger = item.querySelector(TRIGGER_SELECTOR);
    const panel = item.querySelector(PANEL_SELECTOR);

    if (!(trigger instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return null;

    return { trigger, panel };
  }

  /**
   * @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} pair
   */
  #open(pair) {
    clearTimeout(this.#closeTimer);
    this.#closeAll(pair.panel);

    pair.trigger.setAttribute('aria-expanded', 'true');
    pair.panel.hidden = false;
  }

  /**
   * @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} pair
   */
  #close(pair) {
    pair.trigger.setAttribute('aria-expanded', 'false');
    pair.panel.hidden = true;
  }

  /**
   * Closes every panel except the one passed, so only one is ever open.
   *
   * @param {HTMLElement} [except] - A panel to leave alone.
   */
  #closeAll(except) {
    for (const panel of this.refs.panels ?? []) {
      if (panel === except || panel.hidden) continue;

      const pair = this.#pairFrom(panel);
      if (pair) this.#close(pair);
    }
  }

  /**
   * Closes any open panel when a press lands outside the component.
   *
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handleOutsidePress = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#closeAll();
  };

  /**
   * Closes a panel once focus leaves its item entirely, so tabbing past the last link
   * doesn't leave an orphaned panel open behind the user.
   *
   * @param {FocusEvent} event - The focusout event.
   */
  #handleFocusOut = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest(ITEM_SELECTOR);
    if (!item) return;

    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    const pair = this.#pairFrom(item);
    if (pair && !pair.panel.hidden) this.#close(pair);
  };

  /**
   * Sets the header's initial `data-menu-style`.
   *
   * `data-menu-style` is NOT server-rendered — `sections/header.liquid` only shows
   * `.header-menu` at >=750px when it reads 'menu', and the sole thing that sets it on
   * the storefront is `header-menu.js`, which does not load in dropdown mode. Without
   * this the menu would never become visible. Mirrors the decision in
   * `setHeaderMenuStyle()` (`assets/utilities.js`) minus the `<overflow-list>` lookup,
   * which does not apply here.
   */
  #applyBaseMenuStyle() {
    const header = this.closest('header-component');
    if (!(header instanceof HTMLElement)) return;

    header.dataset.menuStyle = isTouchDevice() ? 'drawer' : 'menu';
  }

  /**
   * Moves the menu to its own full-width row once it no longer fits beside the logo and
   * the icons, and brings it back when there is room again.
   *
   * Two earlier attempts measured the wrong thing, so it is worth being explicit about
   * what this one measures and why it can actually differ:
   *
   * - Comparing the menu's own box against its own content failed because they track
   *   each other exactly.
   * - Comparing `.header__columns` scrollWidth against clientWidth failed because the
   *   grid never overflows: `min-inline-size: 0` on this block lets the centre column
   *   shrink below its content, so the menu overflows *inside* its own column while the
   *   grid itself still fits.
   *
   * So: measure the menu's required width directly off the list, and derive the space
   * actually available to it from the container minus what the logo and icons really
   * occupy — their *content* widths, not their track widths, which are `1fr` and so say
   * nothing about how much room they need. Every input is independent of which row state
   * is currently applied, so the comparison gives the same answer in both and cannot
   * oscillate. No hysteresis needed.
   */
  #syncRowLayout = () => {
    const header = this.closest('header-component');
    const columns = this.#columns;
    const list = this.refs.list;

    if (!(header instanceof HTMLElement) || !columns || !list) return;

    // Zero width means hidden — drawer mode, or below the 750px breakpoint.
    if (columns.clientWidth === 0) return;

    const style = getComputedStyle(columns);
    const gap = parseFloat(style.columnGap) || 0;
    const sides =
      contentWidth(columns.querySelector('.header__column--left')) +
      contentWidth(columns.querySelector('.header__column--right'));

    // clientWidth is the padding box, so inline padding has to come off.
    const inner =
      columns.clientWidth -
      (parseFloat(style.paddingInlineStart) || 0) -
      (parseFloat(style.paddingInlineEnd) || 0);

    // Two gaps: this asks "would it fit on ONE row?", which stays the right question
    // even while the two-row layout (with its single gap) is applied.
    const available = inner - sides - gap * 2;
    const required = list.scrollWidth;

    header.dataset.wceMenuRows = required > available + OVERFLOW_TOLERANCE ? '2' : '1';
  };
}

/**
 * The width an element's children actually occupy, ignoring the element's own box.
 *
 * The header's side columns are `1fr` grid tracks, so their `offsetWidth` reflects the
 * space the grid handed them, not the space they need. Summing the laid-out children
 * gives the real figure. Children hidden by CSS (the drawer trigger, in menu mode)
 * contribute nothing.
 *
 * @param {Element | null} element - The column to measure.
 * @returns {number} Content width in pixels.
 */
function contentWidth(element) {
  if (!(element instanceof HTMLElement)) return 0;

  const gap = parseFloat(getComputedStyle(element).columnGap) || 0;
  const children = Array.from(element.children).filter(
    (child) => getComputedStyle(child).display !== 'none'
  );

  const total = children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0);

  return total + gap * Math.max(0, children.length - 1);
}

if (!customElements.get('wce-dropdown-menu-component')) {
  customElements.define('wce-dropdown-menu-component', WceDropdownMenuComponent);
}
